import { useEffect, useMemo } from "react";
import {
  App,
  FolderTreeItem,
  HydratedZapAsset,
  useSplitTreatments,
  Zap,
} from "@zapier/client";
import { ApprovalType } from "@zapier/utils";
import { FolderModal, Table, ZapModal } from "@zapier/shared";
import {
  SortDescriptor,
  useBetaDataTableContext as useTableContext,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import FolderRow from "../../folders/FolderTable/FolderRow";
import ZapRow from "../../zaps/ZapTable/ZapRow";
import styles from "./FolderZapTable.module.css";
import { useBulkSelectItems } from "../FolderZapTable/useBulkSelectItems";

interface Props {
  ariaLabel: string;
  folder?: FolderTreeItem;
  folders: FolderTreeItem[];
  isLoading?: boolean;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  selectedZapIds?: string[];
  setApprovalType: (approvalType: ApprovalType | undefined) => void;
  setCurrentFolder: (folder: FolderTreeItem | undefined) => void;
  setCurrentFolderModal: (modal: FolderModal | undefined) => void;
  setCurrentZap: (zap: Zap | HydratedZapAsset | undefined) => void;
  setCurrentZapModal: (modal: ZapModal | undefined) => void;
  setSelectedZapIds: (zapIds: string[]) => void;
  showCheckboxes?: boolean;
  zaps: Zap[];
  apps?: Record<string, App>;
}

const FolderZapTable = ({
  ariaLabel,
  folder,
  folders,
  isLoading,
  onSortChange,
  selectedZapIds,
  setApprovalType,
  setCurrentFolder,
  setCurrentFolderModal,
  setCurrentZap,
  setCurrentZapModal,
  setSelectedZapIds,
  showCheckboxes = true,
  zaps,
  apps,
}: Props) => {
  const tAssetTypes = useTranslations("asset-types");
  const { selectedRows } = useTableContext();
  const { isLastRunEnabled } = useSplitTreatments();
  useEffect(() => {
    setSelectedZapIds(selectedRows);
  }, [selectedRows, setSelectedZapIds]);

  const bulkSelectItems = useBulkSelectItems({
    selectedZapIds,
    setCurrentZapModal,
    folder,
  });

  const columns = useMemo(() => {
    const cols: { label: string; className: string; sortKey?: string }[] = [
      { label: "Status", sortKey: "status", className: styles.statusColumn },
      { label: "Apps", className: styles.appsColumn },
      { label: "Last modified", className: styles.lastModifiedColumn },
    ];
    if (isLastRunEnabled) {
      cols.push({ label: "Last run", className: styles.lastRunColumn });
    }
    return cols;
  }, [isLastRunEnabled]);

  return (
    <Table
      allowSortByTitle={true}
      ariaLabel={ariaLabel}
      bulkActionsAssetLabel={tAssetTypes("plural.zap")}
      bulkSelectActions={bulkSelectItems}
      columns={columns}
      isLoading={isLoading}
      onSortChange={onSortChange}
      selectableEntities={zaps}
      showBulkSelectPaused={true}
      showCheckboxes={showCheckboxes}
      showOwner={true}
      // No share action wired on these rows — don't reserve share-button
      // width so the Options column stays tight and consistent.
      showShareButton={false}
      folderZapTableRow={true}
    >
      {folders.map((folder, index) => (
        <FolderRow
          folder={folder}
          folderLevel={0}
          folderZapTableRow={true}
          showLastRun={isLastRunEnabled}
          includeSelectionColumn={showCheckboxes}
          key={index}
          setCurrentFolder={setCurrentFolder}
          setCurrentModal={setCurrentFolderModal}
        />
      ))}
      {zaps.map((zap, index) => (
        <ZapRow
          allowSelection={showCheckboxes}
          folderZapTableRow={true}
          showLastRun={isLastRunEnabled}
          key={index}
          setApprovalType={setApprovalType}
          setCurrentModal={setCurrentZapModal}
          setCurrentZap={setCurrentZap}
          zap={zap}
          apps={apps}
        />
      ))}
    </Table>
  );
};
export default FolderZapTable;
